import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchStockissues,
    createStockIssue,
    updateStockIssue,
    deleteStockIssue,
    setPagination,
    setSort,
    searchStockissues,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/stockissue/stockissueActions';
import StockIssueForm from '../../components/pageModule/Stockissues/StockIssueForm';
import CloudActionButtons, { isContactCloudLocked } from '../../components/pageModule/Customers/CloudActionButtons';
import { useHasPermission } from '../../utils/permissionUtils';
import stockissueService from '../../services/stockissueService';

const MySwal = withReactContent(Swal);

const Stockissues = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = []
}) => {

    const hasCreatePermission = useHasPermission(['create stock issues']);
    const hasUpdatePermission = useHasPermission(['update stock issues']);
    const hasViewPermission = useHasPermission(['view stock issues']);

    const stockissueState = useSelector(state => state.stockissue);
    const dispatch = useDispatch();
    const [cloudLoadingByStockIssue, setCloudLoadingByStockIssue] = useState({});

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createStockIssue(formData));
            toast.success('StockIssue created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating stockissue', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateStockIssue(id, formData));
            toast.success('StockIssue updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating stockissue', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteStockIssue(id));
                toast.success('StockIssue deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting stockissue', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleCloudPush = useCallback(async (row) => {
        if (!row?.id) {
            return;
        }

        if (isContactCloudLocked(row?.cloud)) {
            toast.warning('Stock issue cloud sync is locked', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return;
        }

        setCloudLoadingByStockIssue(prev => ({ ...prev, [row.id]: true }));
        try {
            await stockissueService.pushCloud(row.id, {});
            toast.success('Stock issue queued for cloud sync', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            await dispatch(fetchStockissues({}, {}, true, externalCondition));
        } catch (error) {
            toast.error(error.message || 'Error pushing stock issue to cloud', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setCloudLoadingByStockIssue(prev => ({ ...prev, [row.id]: false }));
        }
    }, [dispatch, externalCondition]);

    const columns = useMemo(() => [
        {
            id: 'issue_number',
            name: 'Issue Number',
            selector: row => row.issue_number,
            minWidth: '170px'
        },
        {
            id: 'issue_date',
            name: 'Issue Date',
            selector: row => row.issue_date,
            sortable: true,
            sortField: 'issue_date',
            minWidth: '120px'
        },
        {
            id: 'job_order',
            name: 'Job Order',
            selector: row => row.order_number,
            minWidth: '200px'
        },
        {
            id: 'item',
            name: 'Item',
            selector: row => row.fg_qty + ' x ' + row.item_name,
            grow: 2,
        },
        {
            id: 'issued_to',
            name: 'Issued To',
            selector: row => row.issued_to,
            grow: 2,
        },
        {
            id: 'issued_to_ref',
            name: 'Issued To Ref',
            selector: row => row.issued_to_ref,
            grow: 2,
        },
        // {
        //     id: 'cloud',
        //     name: 'Cloud',
        //     minWidth: '180px',
        //     cell: (row) => (
        //         <CloudActionButtons
        //             row={row}
        //             onPush={handleCloudPush}
        //             isPushing={!!cloudLoadingByStockIssue[row?.id]}
        //         />
        //     ),
        //     ignoreRowClick: true,
        //     allowOverflow: true,
        // },
    ], [handleCloudPush, cloudLoadingByStockIssue]);

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchStockissues(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchStockissues(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    return (
        <>
            <GenericPage
                title="Stock Issues"
                moduleName="StockIssue"
                moduleKey="stockissues"
                state={stockissueState}
                actions={actions}
                FormComponent={StockIssueForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                urlKey="stock_issues"
                actionType="url"
                onSelectedRowsChange={onSelectedRowsChange}
                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Stockissues;
